import { divideAccrualsToAprs } from '.'
import {
  AaveV2UserReserveResponse,
  AaveV3UserReserveResponse,
  CompoundV3UserReserveResponse,
} from '../../../types/lenderTypes'
import { computeSumerWaterfall } from '../../margin/base/sumer'
import { safeDivide } from './formatting'
import { getOraclePrice } from './oraclePrice'
import {
  buildUnderlyingInfo,
  getMarketUidsFromMeta,
  LenderCrossPoolMeta,
  LenderYieldComplete,
  SumerMarketMeta,
  UserData,
} from './types'

type LenderUserData =
  | AaveV3UserReserveResponse
  | AaveV2UserReserveResponse
  | CompoundV3UserReserveResponse

function getSumerMeta(m: LenderYieldComplete): SumerMarketMeta | undefined {
  // Support both new (params.metadata.sumer) and legacy (sumerMeta) data shapes
  const md = m.params?.metadata
  if (md && 'sumer' in md) return (md as { sumer?: SumerMarketMeta }).sumer
  return (m as any).sumerMeta
}

export function createSumerUserState(
  payload: LenderUserData,
  lenderData: LenderCrossPoolMeta,
  totalDeposits24h: number = 0,
  totalDebt24h: number = 0,
): UserData {
  const assetKeys = getMarketUidsFromMeta(lenderData)
  const { chainId, account } = payload
  const isWhitelisted = !Boolean((payload as any)?.notWhitelisted ?? false)

  // Collect unique group rates from all markets (each market carries its own group's rates)
  const groupRatesMap: Record<number, SumerMarketMeta> = {}

  // Per-group accumulators (enabled-only and all-active)
  const gAccum: Record<
    number,
    { cDep: number; sDep: number; cDebt: number; sDebt: number }
  > = {}
  const gAccumAll: Record<
    number,
    { cDep: number; sDep: number; cDebt: number; sDebt: number }
  > = {}

  // organic yields
  let depositInterest = 0
  let borrowInterest = 0
  // rewards
  let rewardDepositAccrual = 0
  let rewardBorrowAccrual = 0
  // staking
  let stakingDepositAccrual = 0
  let stakingBorrowAccrual = 0
  // amounts
  let deposits = 0
  let debt = 0
  let oracleDebt = 0

  let rewardsPerAsset: any = {}

  for (let i = 0; i < assetKeys.length; i++) {
    const marketUid = assetKeys[i]
    if (!lenderData?.[marketUid] || !payload.lendingPositions[marketUid])
      continue

    const pos = payload.lendingPositions[marketUid]
    const { depositsUSD, debtStableUSD, debtUSD, collateralEnabled } = pos
    // oracle values for risk calculations (fallback to general if missing)
    const depositsUSDOracle = (pos as any).depositsUSDOracle ?? depositsUSD
    const debtStableUSDOracle = (pos as any).debtStableUSDOracle ?? debtStableUSD
    const debtUSDOracle = (pos as any).debtUSDOracle ?? debtUSD
    const {
      depositRate,
      intrinsicYield,
      variableBorrowRate,
      stableBorrowRate,
      rewards,
      flags,
    } = lenderData[marketUid]
    const sumerMeta = getSumerMeta(lenderData[marketUid])

    // amounts (display)
    deposits += depositsUSD
    debt += debtStableUSD
    debt += debtUSD
    // oracle-based debt for risk
    oracleDebt += debtStableUSDOracle
    oracleDebt += debtUSDOracle

    // rewards
    ;(rewards ?? []).forEach((rewardData: any) => {
      const key = rewardData.asset
      rewardDepositAccrual += rewardData.depositRate * depositsUSD
      rewardBorrowAccrual +=
        rewardData.variableBorrowRate * debtUSD +
        (rewardData.stableBorrowRate ?? 0) * debtStableUSD

      const rewDepo = rewardData.depositRate * depositsUSD
      const rewDebt =
        rewardData.variableBorrowRate * debtUSD +
        (rewardData.stableBorrowRate ?? 0) * (debtStableUSD ?? 0)

      if (!rewardsPerAsset[key])
        rewardsPerAsset[key] = { depositApr: 0, borrowApr: 0 }
      if (rewDepo > 0) rewardsPerAsset[key].depositApr += rewDepo
      if (rewDebt > 0) rewardsPerAsset[key].borrowApr += rewDebt
    })

    // staking
    stakingDepositAccrual += (intrinsicYield ?? 0) * depositsUSD
    stakingBorrowAccrual += (intrinsicYield ?? 0) * (debtStableUSD + debtUSD)

    // Sumer group accumulation
    if (sumerMeta) {
      const gId = sumerMeta.groupId
      if (!groupRatesMap[gId]) groupRatesMap[gId] = sumerMeta
      if (!gAccum[gId]) gAccum[gId] = { cDep: 0, sDep: 0, cDebt: 0, sDebt: 0 }
      if (!gAccumAll[gId])
        gAccumAll[gId] = { cDep: 0, sDep: 0, cDebt: 0, sDebt: 0 }

      const totalDebtForPos = debtUSDOracle + debtStableUSDOracle
      if (sumerMeta.isCToken) {
        if (flags?.collateralActive && collateralEnabled)
          gAccum[gId].cDep += depositsUSDOracle
        if (flags?.collateralActive) gAccumAll[gId].cDep += depositsUSDOracle
        gAccum[gId].cDebt += totalDebtForPos
        gAccumAll[gId].cDebt += totalDebtForPos
      } else {
        if (flags?.collateralActive && collateralEnabled)
          gAccum[gId].sDep += depositsUSDOracle
        if (flags?.collateralActive) gAccumAll[gId].sDep += depositsUSDOracle
        gAccum[gId].sDebt += totalDebtForPos
        gAccumAll[gId].sDebt += totalDebtForPos
      }
    }

    // IRs
    depositInterest += depositRate! * depositsUSD
    borrowInterest +=
      debtStableUSD * stableBorrowRate! + debtUSD * variableBorrowRate!
  }

  // Compute collateral via absorption waterfall
  const borrowDiscountedCollateral = computeSumerWaterfall(
    Object.values(groupRatesMap),
    gAccum,
  )
  const borrowDiscountedCollateralAllActive = computeSumerWaterfall(
    Object.values(groupRatesMap),
    gAccumAll,
  )

  const nav = deposits - debt
  const balanceData = {
    borrowDiscountedCollateral,
    borrowDiscountedCollateralAllActive,
    collateral: borrowDiscountedCollateral,
    collateralAllActive: borrowDiscountedCollateralAllActive,
    deposits,
    debt,
    adjustedDebt: oracleDebt,
    nav,
    deposits24h: totalDeposits24h,
    debt24h: totalDebt24h,
    nav24h: totalDeposits24h - totalDebt24h,
    ...(payload.rewards ? { rewards: payload.rewards } : {}),
  }

  const aprData = {
    apr: safeDivide(depositInterest - borrowInterest, nav),
    borrowApr: safeDivide(borrowInterest, debt),
    depositApr: safeDivide(depositInterest, deposits),
    rewards: divideAccrualsToAprs(rewardsPerAsset, nav, deposits, debt),
    rewardApr: safeDivide(rewardDepositAccrual + rewardBorrowAccrual, nav),
    rewardDepositApr: safeDivide(rewardDepositAccrual, deposits),
    rewardBorrowApr: safeDivide(rewardBorrowAccrual, debt),
    intrinsicApr: safeDivide(stakingDepositAccrual - stakingBorrowAccrual, nav),
    intrinsicDepositApr: safeDivide(stakingDepositAccrual, deposits),
    intrinsicBorrowApr: safeDivide(stakingBorrowAccrual, debt),
  }
  const userConfig = { selectedMode: '0', id: account, isWhitelisted }

  const creditLine = Math.max(
    0,
    borrowDiscountedCollateral - balanceData.adjustedDebt,
  )

  // Per-position withdrawable / borrowable
  for (let i = 0; i < assetKeys.length; i++) {
    const marketUid = assetKeys[i]
    if (!lenderData?.[marketUid]) continue

    const {
      configs,
      flags,
      borrowLiquidity,
      withdrawLiquidity,
    } = lenderData[marketUid]
    const sm = getSumerMeta(lenderData[marketUid])
    // Use the asset's own groupId for config lookup (gives intra rate)
    const config = configs?.[sm?.groupId ?? 0]
    const price = getOraclePrice(lenderData[marketUid])
    const bcf = config?.borrowCollateralFactor ?? 1
    const bf = config?.borrowFactor ?? 1

    const pos = payload.lendingPositions[marketUid]

    if (pos) {
      let withdrawable: string
      if (!pos.collateralEnabled || config?.collateralDisabled) {
        withdrawable = String(pos.deposits)
      } else if (balanceData.debt === 0) {
        withdrawable = String(pos.deposits)
      } else {
        const withdrawableUSD = Math.max(creditLine / bcf, 0)
        withdrawable = String(
          Math.min(withdrawableUSD / price, Number(pos.deposits)),
        )
      }
      // cap by protocol-level withdraw liquidity
      if (withdrawLiquidity != null) {
        withdrawable = String(Math.min(Number(withdrawable), withdrawLiquidity))
      }

      let borrowable: string
      if (!flags?.borrowingEnabled || flags?.isFrozen || config?.debtDisabled) {
        borrowable = '0'
      } else {
        const borrowableUSD = Math.max(creditLine / bf, 0)
        borrowable = String(borrowableUSD / price)
      }
      // cap by protocol-level borrow liquidity
      if (borrowLiquidity != null) {
        borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
      }

      ;(pos as any).withdrawable = withdrawable
      ;(pos as any).borrowable = borrowable
      ;(pos as any).underlyingInfo = buildUnderlyingInfo(lenderData[marketUid])
    } else if (
      (deposits > 0 || debt > 0) &&
      flags?.borrowingEnabled &&
      !flags?.isFrozen &&
      !config?.debtDisabled
    ) {
      let borrowable = String(Math.max(creditLine / bf, 0) / price)
      // cap by protocol-level borrow liquidity
      if (borrowLiquidity != null) {
        borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
      }

      payload.lendingPositions[marketUid] = {
        marketUid,
        deposits: '0',
        debt: '0',
        debtStable: '0',
        depositsUSD: 0,
        debtUSD: 0,
        debtStableUSD: 0,
        collateralEnabled: false,
        claimableRewards: 0,
        withdrawable: '0',
        borrowable,
        underlyingInfo: buildUnderlyingInfo(lenderData[marketUid]),
      } as any
    }
  }

  return {
    lender: '',
    account,
    chainId,
    data: [
      {
        health:
          balanceData.debt === 0
            ? null
            : balanceData.adjustedDebt > 0
              ? balanceData.borrowDiscountedCollateral /
                balanceData.adjustedDebt
              : balanceData.collateral / balanceData.debt,
        borrowCapacityUSD: creditLine,
        accountId: '0',
        balanceData,
        aprData,
        userConfig,
        positions: Object.values(payload.lendingPositions) as any,
      },
    ],
  }
}
